import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, Card, IconButton, Text } from 'react-native-paper';
import { colors, spacing, typography } from '../../../constants/theme';
import { ReviewCategory, ReviewItem } from '../AdminDashboard.types';

interface AdminCategoryAlertButton {
  text: string;
  style?: 'default' | 'cancel' | 'destructive';
  onPress?: () => void;
}

interface AdminCategoriesSectionProps {
  categories: ReviewCategory[];
  expandedCategoryIds: Set<number>;
  items: ReviewItem[];
  onCreateCategory: () => void;
  onDeleteCategory: (categoryId: number, moveToCategory?: number) => void;
  onEditCategory: (category: ReviewCategory) => void;
  onReorderCategory: (categoryId: number, direction: 'up' | 'down', parentCategoryId?: number | null) => void;
  onShowAlert: (title: string, message: string, buttons: AdminCategoryAlertButton[]) => void;
  onToggleCategoryExpansion: (categoryId: number) => void;
  selectedTemplateId: number | null;
}

const AdminCategoriesSection: React.FC<AdminCategoriesSectionProps> = ({
  categories,
  expandedCategoryIds,
  items,
  onCreateCategory,
  onDeleteCategory,
  onEditCategory,
  onReorderCategory,
  onShowAlert,
  onToggleCategoryExpansion,
  selectedTemplateId,
}) => {
  const renderCategoryCard = (category: ReviewCategory, level: number = 0) => {
    const itemsInCategory = items.filter(item => item.categoryId === category.id);
    const subcategories = categories.filter(c => c.parentCategoryId === category.id)
      .sort((a, b) => a.displayOrder - b.displayOrder);
    const hasSubcategories = subcategories.length > 0;
    const isExpanded = expandedCategoryIds.has(category.id);
    const parentCategory = category.parentCategoryId
      ? categories.find(c => c.id === category.parentCategoryId)
      : null;

    return (
      <Card key={category.id} style={[styles.card, level > 0 && styles.nestedCard]}>
        <Card.Content>
          <View style={styles.itemHeader}>
            <View style={styles.itemInfo}>
              <View style={styles.categoryNameRow}>
                {hasSubcategories && (
                  <Button
                    mode="text"
                    onPress={() => onToggleCategoryExpansion(category.id)}
                    style={styles.expandButton}
                    icon={isExpanded ? 'chevron-down' : 'chevron-right'}
                    compact
                  >
                    {null}
                  </Button>
                )}
                <Text style={[styles.itemName, { marginLeft: hasSubcategories ? 0 : 40 }]}>
                  {category.name}
                </Text>
              </View>
              {parentCategory && (
                <Text style={[styles.categoryName, { marginLeft: 40 }]}>Parent: {parentCategory.name}</Text>
              )}
              {category.description && (
                <Text style={[styles.description, { marginLeft: 40 }]}>{category.description}</Text>
              )}
            </View>
            <View style={styles.itemActions}>
              <View style={styles.reorderButtons}>
                <IconButton
                  icon="chevron-up"
                  size={20}
                  onPress={() => onReorderCategory(category.id, 'up', category.parentCategoryId)}
                  style={styles.reorderIconButton}
                  mode="outlined"
                />
                <IconButton
                  icon="chevron-down"
                  size={20}
                  onPress={() => onReorderCategory(category.id, 'down', category.parentCategoryId)}
                  style={styles.reorderIconButton}
                  mode="outlined"
                />
              </View>
              <Button
                mode="outlined"
                onPress={() => onEditCategory(category)}
                style={styles.editButton}
              >
                Edit
              </Button>
              <Button
                mode="outlined"
                onPress={() => {
                  if (itemsInCategory.length > 0) {
                    const availableCategories = categories.filter(c => c.id !== category.id && c.templateId === selectedTemplateId);

                    onShowAlert(
                      'Delete Category',
                      `This category contains ${itemsInCategory.length} items. Where should they be moved?`,
                      (availableCategories.map(c => ({
                        text: c.name,
                        onPress: () => onDeleteCategory(category.id, c.id),
                      })) as AdminCategoryAlertButton[]).concat([{ text: 'Cancel', style: 'cancel' }])
                    );
                  } else {
                    onShowAlert(
                      'Delete Category',
                      'Are you sure you want to delete this category?',
                      [
                        { text: 'Cancel', style: 'cancel' },
                        {
                          text: 'Delete',
                          style: 'destructive',
                          onPress: () => onDeleteCategory(category.id),
                        },
                      ]
                    );
                  }
                }}
                style={styles.deprecateButton}
              >
                Delete
              </Button>
            </View>
          </View>

          <View style={styles.itemStats}>
            <Text style={styles.stat}>
              Items: {itemsInCategory.length}
            </Text>
            {hasSubcategories && (
              <Text style={styles.stat}>
                Subcategories: {subcategories.length}
              </Text>
            )}
          </View>
        </Card.Content>

        {hasSubcategories && isExpanded && (
          <View style={styles.subcategoriesContainer}>
            {subcategories.map(subcat => renderCategoryCard(subcat, level + 1))}
          </View>
        )}
      </Card>
    );
  };

  const topLevelCategories = categories
    .filter(cat => !cat.parentCategoryId)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Categories ({categories.length})</Text>
        <Button
          mode="contained"
          onPress={onCreateCategory}
          style={styles.createButton}
        >
          + New Category
        </Button>
      </View>

      {topLevelCategories.length > 0 ? (
        topLevelCategories.map(cat => renderCategoryCard(cat, 0))
      ) : (
        <Text style={styles.stat}>No categories found</Text>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    ...typography.h2,
    marginBottom: spacing.md,
  },
  createButton: {},
  card: {
    marginBottom: spacing.md,
  },
  nestedCard: {
    marginLeft: spacing.xl,
    backgroundColor: colors.surface,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary + '30',
  },
  subcategoriesContainer: {
    marginTop: spacing.xs,
    paddingLeft: spacing.md,
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  itemInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  categoryNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  expandButton: {
    marginRight: spacing.xs,
    padding: 0,
    minWidth: 40,
  },
  itemName: {
    ...typography.h3,
    marginBottom: spacing.xs,
  },
  categoryName: {
    ...typography.caption,
    color: colors.primary,
    marginBottom: spacing.xs,
  },
  description: {
    ...typography.body,
    color: colors.textSecondary,
  },
  itemActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  reorderButtons: {
    flexDirection: 'column',
    gap: spacing.xs,
    marginRight: spacing.sm,
  },
  reorderIconButton: {
    margin: 0,
    borderWidth: 1,
    borderColor: colors.gray300,
  },
  editButton: {
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deprecateButton: {
    borderColor: colors.error,
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemStats: {
    flexDirection: 'row',
    marginTop: spacing.md,
    gap: spacing.md,
  },
  stat: {
    ...typography.caption,
    color: colors.textSecondary,
  },
});

export default AdminCategoriesSection;
